import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { adminGet, adminPost } from './adminApi';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { PinnedReportsStrip } from './reports/PinnedReportsStrip';
import { ClockDriftCard } from './qr/ClockDriftCard';

const mono = { fontFamily: "'IBM Plex Mono', monospace" };

const ALERT_BADGE = {
  LABEL_REUSE: { label: 'Label reuse', bg: '#FBEBEB', fg: '#8C2B2B' },
  EXCURSION: { label: 'Excursion', bg: '#FBEBEB', fg: '#8C2B2B' },
  CLOCK_EXCEEDED: { label: 'Clock exceeded', bg: '#FBF3E6', fg: '#8C6A2B' },
  STAGE_SKIPPED: { label: 'Stage skipped', bg: '#FBF3E6', fg: '#8C6A2B' },
};

function PassportAlertsFeed() {
  const [alerts, setAlerts] = useState([]);
  const [showAcked, setShowAcked] = useState(false);
  const load = useCallback(() => {
    adminGet('/alerts').then(setAlerts).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const ack = async (a) => {
    try {
      await adminPost(`/alerts/${a.id}/ack`, {});
      toast.success('Alert acknowledged');
      load();
    } catch (e) { toast.error(e.message); }
  };

  const rows = alerts.filter(a => showAcked || !a.acknowledged_at).slice(0, 8);
  const openCount = alerts.filter(a => !a.acknowledged_at).length;
  return (
    <div className="rounded-lg border bg-white p-5 mb-6" style={{ borderColor: '#E5E3DC' }} data-testid="passport-alerts-feed">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold" style={{ color: '#1B1D1C' }}>
          Passport alerts {openCount > 0 && <span className="ml-2 px-2 py-0.5 rounded-full text-xs align-middle" style={{ background: '#FBEBEB', color: '#8C2B2B' }}>{openCount} open</span>}</h2>
        <button data-testid="toggle-acked-alerts" onClick={() => setShowAcked(!showAcked)}
          className="text-xs underline" style={{ color: '#6b7069' }}>{showAcked ? 'Hide acknowledged' : 'Show acknowledged'}</button>
      </div>
      {rows.length === 0 ? <p className="text-sm" style={{ color: '#9a9e96' }}>No open passport alerts — label reuse, excursions and skipped stages will appear here</p> : (
        <table className="w-full text-sm">
          <thead><tr className="text-left border-b" style={{ color: '#6b7069', borderColor: '#EDEBE4' }}>
            <th className="py-2 font-medium">Type</th><th className="font-medium">Passport</th>
            <th className="font-medium">Detail</th><th className="font-medium">When</th><th></th></tr></thead>
          <tbody>
            {rows.map(a => {
              const b = ALERT_BADGE[a.type] || { label: a.type, bg: '#EFEDE6', fg: '#4b504a' };
              return (
                <tr key={a.id} className="border-b" style={{ borderColor: '#F3F1EA', opacity: a.acknowledged_at ? 0.5 : 1 }}
                  data-testid={`p3-alert-row-${a.id}`}>
                  <td className="py-2.5 pr-2"><span className="px-2 py-0.5 rounded text-xs whitespace-nowrap" style={{ background: b.bg, color: b.fg }}>{b.label}</span></td>
                  <td style={mono}>{a.passport_short_code || '—'}</td>
                  <td className="pr-3 max-w-md truncate" title={a.message}>{a.message}</td>
                  <td className="whitespace-nowrap" style={{ color: '#6b7069' }}>{new Date(a.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                  <td className="text-right">
                    {a.acknowledged_at
                      ? <span className="text-xs" style={{ color: '#9a9e96' }}>Acked</span>
                      : <button data-testid={`ack-alert-${a.id}`} onClick={() => ack(a)}
                          className="text-xs rounded-md border px-2 py-1 bg-white hover:bg-[#FAF9F6]"
                          style={{ borderColor: '#D9D6CD', color: '#0B6B55' }}>Acknowledge</button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Card({ title, value, sub, alertStyle, testId }) {
  return (
    <div data-testid={testId} className="rounded-lg border p-5 bg-white"
      style={alertStyle ? { background: '#FBEBEB', borderColor: '#EBC8C8' } : { borderColor: '#E5E3DC' }}>
      <p className="text-sm" style={{ color: alertStyle ? '#8C2B2B' : '#6b7069' }}>{title}</p>
      <p className="text-4xl font-bold mt-1" style={{ ...mono, color: alertStyle ? '#8C2B2B' : '#1B1D1C' }}>{value}</p>
      <p className="text-xs mt-1" style={{ color: alertStyle ? '#8C2B2B' : '#9a9e96' }}>{sub}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    adminGet('/dashboard').then(setData).catch(e => setErr(e.message));
  }, []);

  if (err) return <div className="p-8 text-red-700" data-testid="admin-dashboard-error">{err}</div>;
  if (!data) return <div className="p-8" data-testid="admin-dashboard-loading">Loading…</div>;

  const { cards, pipeline, latest_alerts, sim_renewals } = data;
  const pipelineSteps = [
    ['1 · Received', pipeline.received], ['2 · SIM bound', pipeline.sim_bound],
    ['3 · QC passed', pipeline.qc_passed], ['4 · Firmware set', pipeline.firmware_set],
    ['5 · Allocated', pipeline.allocated], ['6 · Active', pipeline.active],
  ];

  return (
    <div className="p-8 max-w-7xl" data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-4xl font-bold" style={{ color: '#1B1D1C' }}>Dashboard</h1>
        <div className="flex gap-3">
          <Button variant="outline" data-testid="dash-register-device-btn" onClick={() => navigate('/admin/devices/receive')}
            className="bg-white border-[#D9D6CD]">Receive devices</Button>
          <Button data-testid="dash-add-customer-btn" onClick={() => navigate('/admin/tenants/new')}
            className="text-white hover:opacity-90" style={{ background: '#0B6B55' }}>Add tenant</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card testId="card-in-stock" title="Devices in stock" value={cards.in_stock} sub="QC passed, unallocated" />
        <Card testId="card-awaiting-qc" title="Awaiting QC" value={cards.awaiting_qc} sub="SIM bound, not tested" />
        <Card testId="card-active" title="Active with customers" value={cards.active} sub={`Across ${cards.active_tenants} customers`} />
        <Link to="/admin/alerts" data-testid="card-alerts-link" className="block"><Card testId="card-alerts" title="Open alerts" value={cards.open_alerts} sub={cards.open_alerts_by_source ? `${cards.open_alerts_by_source.device_alert} device · ${cards.open_alerts_by_source.passport_alert} passport · ${cards.open_alerts_by_source.qr_exception} QR → Alerts` : 'Device, passport and QR → Alerts'} alertStyle /></Link>
      </div>

      <PinnedReportsStrip />
      <ClockDriftCard />

      <PassportAlertsFeed />

      <div className="rounded-lg border bg-white p-5 mb-6" style={{ borderColor: '#E5E3DC' }} data-testid="lifecycle-pipeline">
        <h2 className="text-lg font-bold mb-4" style={{ color: '#1B1D1C' }}>Device lifecycle pipeline</h2>
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-3">
          {pipelineSteps.map(([label, n], i) => (
            <div key={label} className="rounded-md p-3 border"
              style={i === 5 ? { background: '#E7F2EE', borderColor: '#BFDCD2' } : { background: '#FAF9F6', borderColor: '#EDEBE4' }}>
              <p className="text-xs" style={{ color: '#6b7069' }}>{label}</p>
              <p className="text-2xl font-bold" style={mono}>{n}</p>
            </div>
          ))}
        </div>
        <p className="text-sm mt-3" style={{ color: '#6b7069' }}>QC failed → RMA / return to manufacturer: <span style={mono}>{pipeline.rma}</span></p>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-lg border bg-white p-5" style={{ borderColor: '#E5E3DC' }} data-testid="latest-alerts">
          <h2 className="text-lg font-bold mb-3" style={{ color: '#1B1D1C' }}>Latest alerts <Link to="/admin/alerts" data-testid="latest-alerts-link" className="text-xs font-normal underline ml-2" style={{ color: '#0B6B55' }}>open Alerts</Link></h2>
          {latest_alerts.length === 0 ? <p className="text-sm" style={{ color: '#9a9e96' }}>No open alerts</p> : (
            <table className="w-full text-sm">
              <thead><tr className="text-left border-b" style={{ color: '#6b7069', borderColor: '#EDEBE4' }}>
                <th className="py-2 font-medium">Device</th><th className="font-medium">Customer</th><th className="font-medium">Event</th><th className="font-medium">Time</th></tr></thead>
              <tbody>
                {latest_alerts.map((a, i) => (
                  <tr key={`${a.device}-${a.time}-${i}`} className="border-b" style={{ borderColor: '#F3F1EA' }}>
                    <td className="py-2.5" style={mono}>{a.device || '—'}</td>
                    <td>{a.customer || '—'}</td>
                    <td><span className="px-2 py-0.5 rounded text-xs"
                      style={a.level === 'critical' ? { background: '#FBEBEB', color: '#8C2B2B' } : { background: '#FBF3DE', color: '#7A5C13' }}>{a.event}</span></td>
                    <td style={{ color: '#6b7069' }}>{a.time ? new Date(a.time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="rounded-lg border bg-white p-5" style={{ borderColor: '#E5E3DC' }} data-testid="sim-renewals">
          <h2 className="text-lg font-bold mb-3" style={{ color: '#1B1D1C' }}>SIM and subscription renewals due</h2>
          {sim_renewals.length === 0 ? <p className="text-sm" style={{ color: '#9a9e96' }}>Nothing due in 30 days</p> : (
            <table className="w-full text-sm">
              <thead><tr className="text-left border-b" style={{ color: '#6b7069', borderColor: '#EDEBE4' }}>
                <th className="py-2 font-medium">Device</th><th className="font-medium">MSISDN</th><th className="font-medium">Customer</th><th className="font-medium">Due</th></tr></thead>
              <tbody>
                {sim_renewals.map((rw) => (
                  <tr key={rw.device} className="border-b" style={{ borderColor: '#F3F1EA' }}>
                    <td className="py-2.5"><Link to="/admin/devices" className="hover:underline" style={mono}>{rw.device}</Link></td>
                    <td style={mono}>{rw.msisdn || '—'}</td>
                    <td>{rw.customer}</td>
                    <td style={{ color: '#6b7069' }}>{new Date(rw.due).toLocaleDateString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
